<template>
	<div class="row">
		<div class="col-12 col-lg-4">

			<h6 class="text-uppercase text-muted">Filename/Version</h6>
			<div v-if="creativeAsset.media.length" class="dropdown mb-4">
				<a class="py-3 btn btn-white dropdown-toggle text-start d-flex align-items-center" href="#" role="button" data-bs-toggle="dropdown">
					<div>{{ strLimit(selectedMedia.original_name, 24) }}</div>
					<div class="badge bg-light mb-n1 ms-auto">
						v{{ this.selectedMedia.version }}
					</div>
				</a>
				<div class="dropdown-menu w-100 bg-lighter">
					<h6 class="dropdown-header">Versions</h6>
					<a v-for="(version, index) in this.creativeAsset.media"
						href="#"
						@click.prevent="this.selectedMedia = version; resetPolling();"
						:class="this.selectedMedia.id == version.id
							? 'dropdown-item py-3 active bg-white w-100 d-flex align-items-center'
							: 'dropdown-item py-3 w-100 d-flex align-items-center'">
						{{ strLimit(version.original_name, 24) }}
						<span class="badge bg-light ms-auto">
							v{{ version.version }}
						</span>
					</a>
				</div>
			</div><!--/.dropdown-->
			<div v-else class="d-flex align-items-center">
				<div class="fw-bold">{{ this.selectedMedia.original_name }}</div>
				<div class="badge bg-light ms-2">v{{ this.selectedMedia.version }}</div>
			</div>

			<div v-if="this.selectedMedia.uploader">
				<h6 class="text-uppercase text-muted">Uploaded</h6>
				<p class="small mb-4">
					{{ formatDate(this.selectedMedia.created_at) }}
					by {{ this.selectedMedia.uploader.name }}
				</p>
			</div>

			<div v-if="this.selectedMedia.width">
				<h6 class="text-uppercase text-muted">Dimensions</h6>
				<p class="small mb-4">
					<span v-if="Math.abs(this.selectedMedia.video_rotation) == 90">
						{{ this.selectedMedia.height }} x {{ this.selectedMedia.width }}
					</span>
					<span v-else>
						{{ this.selectedMedia.width }} x {{ this.selectedMedia.height }}
					</span>
				</p>

				<div v-if="this.selectedMedia.aggregate_type == 'video'">
					<h6 class="text-uppercase text-muted">Video Data</h6>
					<p class="small mb-4">
						Video Codec: {{ this.selectedMedia.video_codec }}<br>
						FPS: {{ this.selectedMedia.video_framerate }}<br>
						Bitrate: {{ this.selectedMedia.video_bitrate_mbps }}mbps<br>
						Color Space: {{ this.selectedMedia.video_color_space ? this.selectedMedia.video_color_space : '?' }}<br>
						Audio Codec: {{ this.selectedMedia.audio_codec ? this.selectedMedia.audio_codec : 'none' }}<br>
					</p>
				</div>
				<div v-if="this.selectedMedia.exif && this.selectedMedia.exif.Model">
					<h6 class="text-uppercase text-muted">Camera</h6>
					<p class="small mb-4">
						{{ this.selectedMedia.exif.Model }}
						<span v-if="this.selectedMedia.exif.Make">
							({{ this.selectedMedia.exif.Make }})
						</span>
					</p>
				</div>
				<div v-if="this.selectedMedia.exif && this.selectedMedia.exif.Software">
					<h6 class="text-uppercase text-muted">Software</h6>
					<p class="small mb-4">
						{{ this.selectedMedia.exif.Software }}
					</p>
				</div>

			</div>

		</div>

		<div class="col-12 col-lg-8">

			<!-- Card -->
			<div class="card">
				<div class="card-body">

					<div v-if="isReplacing">
						<div class="mb-3 text-center ratio ratio-4x3 bg-secondary rounded">
							<div class="d-flex flex-column justify-content-center bg-white rounded text-start border p-4">

								<drag-drop :uppy="uppy"/>

								<div class="list-group list-group-flush bg-white mt-3">
									<div v-for="(file, index) in this.files" :key="file.id" class="list-group-item">
										<h4 class="mb-1">{{ file.name }}</h4>
										<small class="text-muted">{{ formatBytes(file.size) }}</small>
										<div class="progress progress-sm mt-3">
											<div class="progress-bar" role="progressbar"
												:style="'width: ' + file.progress.percentage + '%'"
												:aria-valuenow="file.progress.percentage"
												aria-valuemin="0" aria-valuemax="100"></div>
										</div>
									</div>
								</div>

							</div>
						</div>
					</div>

					<!-- Media -->
					<div v-else-if="isEmbeddableImage">
						<div class="mb-3 bg-secondary rounded ratio ratio-1x1">
							<a class="w-100 h-100" @click.prevent="openImageLightbox('#media')" href="#">

								<img :src="this.selectedMedia.embeddable.url"
									:class="this.selectedMedia.embeddable.width >= this.selectedMedia.embeddable.height
										? 'position-absolute w-100 h-auto top-50 start-50 translate-middle'
										: 'position-absolute h-100 w-auto top-50 start-50 translate-middle'"
									id="media">

								<div v-if="this.selectedMedia.approved_at">
									<div class="position-absolute top-0 end-0 px-3 py-2 me-3 mt-3 bg-success text-white small fw-bold rounded-pill">
										<i class="fe fe-check-circle me-1"></i>
										<span class="me-1">Approved</span>
									</div>
								</div>

							</a>
						</div>
					</div>

					<div v-else-if="isEmbeddableVideo && this.selectedMedia.poster">
						<div class="mb-3 bg-secondary rounded ratio ratio-1x1">
							<a class="w-100 h-100" @click.prevent="openVideoLightbox('#media')" href="#">

								<img :src="this.selectedMedia.poster.url" id="media"
									:class="this.selectedMedia.poster.width >= this.selectedMedia.poster.height
										? 'position-absolute w-100 h-auto top-50 start-50 translate-middle'
										: 'position-absolute h-100 w-auto top-50 start-50 translate-middle'">

								<img src="/img/play-overlay.png" style="width:20%;height:auto;"
									class="position-absolute top-50 start-50 translate-middle">

								<div v-if="this.selectedMedia.approved_at">
									<div class="position-absolute top-0 end-0 px-3 py-2 me-3 mt-3 bg-success text-white small fw-bold rounded-pill">
										<i class="fe fe-check-circle me-1"></i>
										<span class="me-1">Approved</span>
									</div>
								</div>

							</a>
						</div>
					</div>

					<div v-else-if="isEmbeddableVideo && !this.selectedMedia.poster">
						<div class="mb-3 text-center ratio ratio-1x1 bg-secondary rounded">
							<a class="w-100 h-100" @click.prevent="openVideoLightbox('#media')" href="#">

								<img src="/img/play-overlay.png" style="width:20%;height:auto;"
									class="position-absolute top-50 start-50 translate-middle">

							</a>
						</div>
					</div>

					<div v-else-if="this.selectedMedia.aggregate_type == 'audio'">
						<div class="mb-3 text-center ratio ratio-1x1 bg-secondary rounded">
							<div class="d-flex flex-column align-items-center justify-content-center">
								<div class="text-white mb-3"><i class="fe fe-music"></i></div>
								<div class="fw-bold text-white">Audio</div>
							</div>
						</div>
					</div>

					<div v-else-if="this.selectedMedia.processing_job_status == 'failed'">
						<div class="mb-3 text-center ratio ratio-1x1 bg-secondary rounded">
							<div class="d-flex flex-column align-items-center justify-content-center">
								<div class="text-white mb-3"><i class="fe fe-eye-off"></i></div>
								<div class="fw-bold text-white">Could not create embeddable media</div>
							</div>
						</div>
					</div>

					<div v-else>
						<div class="mb-3 text-center ratio ratio-1x1 bg-secondary rounded">
							<div class="d-flex flex-column align-items-center justify-content-center">
								<div class="spinner-border text-white mb-3" role="status">
									<span class="visually-hidden">Loading...</span>
								</div>
								<div class="fw-bold text-white">Processing media...</div>
							</div>
						</div>
					</div>

					<div class="row">
						<div class="col">
							<a @click.prevent="isReplacing = !isReplacing" class="btn btn-lg btn-white w-100 rounded-pill" href="#">
								<i class="fe fe-refresh-cw me-1"></i>
								Replace with a New Version
							</a>
						</div>
						<div class="col">
							<a :href="this.creativeAssetEndpoint + '/media/' + this.selectedMedia.id + '/download'" class="btn btn-lg btn-white w-100 rounded-pill">
								<i class="fe fe-download me-1"></i>
								Download Original <span class="text-muted">({{ formatBytes(this.selectedMedia.size) }})</span>
							</a>
						</div>
					</div><!--/.row-->

					<div v-if="creativeAsset.title || creativeAsset.caption">

						<div class="row">
							<div class="col">
								<hr class="my-4">
							</div>
						</div>

						<div class="row">
							<div class="col">
								<div v-if="creativeAsset.title" class="h3">
									{{ creativeAsset.title }}
								</div>
								<div v-if="creativeAsset.caption"
									class="text-secondary"
									v-html="nl2br(linkify(creativeAsset.caption))"
								></div>
							</div><!--/.col-->
						</div><!--/.row-->

					</div><!--/v-if-->

				</div><!--/.card-body-->
			</div><!--/.card-->

		</div><!--/.col-->
	</div><!--/.row-->

	<div class="row">
		<div class="col-12">
			<hr class="my-5">
		</div>
	</div><!--/.row-->

	<div class="row">
		<div class="col-12 col-lg-3">

			<h3>Feedback &amp; Discussion</h3>

		</div>
		<div class="col-12 col-lg-9">

			<creator-deliverable-creative-asset-comments
				:creative-asset-data="this.creativeAsset"
				:creative-asset-endpoint="this.creativeAssetEndpoint"
			></creator-deliverable-creative-asset-comments>

		</div>
	</div>

</template>

<script>
	import BigPicture from 'bigpicture';

	import CreatorDeliverableCreativeAssetComments from './CreatorDeliverableCreativeAssetComments.vue';

	import dayjs from 'dayjs';

	import Uppy from '@uppy/core';
	import AwsS3Multipart from '@uppy/aws-s3-multipart';
	import { DragDrop } from '@uppy/vue';


	import '@uppy/core/dist/style.min.css';
	import '@uppy/drag-drop/dist/style.min.css';
	import '@uppy/progress-bar/dist/style.min.css';


	export default {

		props: ['deliverableData', 'creativeAssetData', 'creativeAssetEndpoint'],

		components: {
			'drag-drop': DragDrop,
			'creator-deliverable-creative-asset-comments': CreatorDeliverableCreativeAssetComments,
		},

		data() {
			return {
				deliverable: this.deliverableData,
				creativeAsset: this.creativeAssetData,
				selectedMedia: this.creativeAssetData.media[0],
				isReplacing: false,
				polling: null,
				files: [],
			}
		},

		mounted() {

			this.resetPolling();

			// register event listeners
			this.uppy.on('file-added', (file) => {
				console.log('Added file', file);
				this.files.push(file);
			});
			this.uppy.on('upload', (data) => {
				// data object consists of `id` with upload ID and `fileIDs` array
				// with file IDs in current upload
				// data: { id, fileIDs }
				console.log('upload', data);
			});
			this.uppy.on('upload-progress', (file, progress) => {
				for (var i = 0; i < this.files.length; i++)
				{
					// console.log('files[i]', this.files[i]);
					if(this.files[i].id == file.id) {
						this.files[i].progress.percentage = progress.bytesUploaded / progress.bytesTotal * 100;
					}
				}
			});
			this.uppy.on('upload-success', (file, response) => {
				// console.log('upload-success', file, response);
				this.handleUploadSuccess(file, response);
			});
			this.uppy.on('complete', (result) => {
				console.log('complete');
				console.log('successful files:', result.successful);
				console.log('failed files:', result.failed);
			});
			this.uppy.on('error', (error) => {
				console.log('error');
				console.error(error);
			});
			this.uppy.on('upload-error', (file, error, response) => {
				console.log('upload-error');
				console.log('error with file:', file.id);
				console.log('error message:', error);
			});

		},

		computed: {

			uppy() {
				// initialize Uppy
				const uppy = new Uppy({
					// https://uppy.io/docs/uppy/
					id: 'uppyCreativeAsset' + this.creativeAsset.id,
					autoProceed: true,
					// allowMultipleUploadBatches: true,
					debug: true,
					inline: true,
					meta: {
						creativeAssetId: this.creativeAsset.id,
					},
					restrictions: {
						allowedFileTypes: this.allowedFileTypes,
						maxFileSize: 1000 * 1000 * 1000 * 1000 * 2, /* 2 GB */
						maxNumberOfFiles: 1,
						minNumberOfFiles: 1,
					}
				}).use(AwsS3Multipart, {
					limit: 5,
					companionUrl: '/',
					companionHeaders:
					{
						'X-CSRF-TOKEN': window.csrfToken,
					},
				});

				return uppy;
			},

			allowedFileTypes() {
				if(this.deliverable.type == 'video') {
					return ['video/*', 'audio/*'];
				}
				else if(this.deliverable.type == 'image') {
					return ['image/*'];
				}
				else {
					return ['image/*', 'video/*', 'audio/*'];
				}
			},

			isEmbeddableVideo() {
				if(this.selectedMedia.aggregate_type == 'video' && this.selectedMedia.embeddable) {
					return true;
				}
				return false;
			},

			isEmbeddableImage() {
				if(this.selectedMedia.aggregate_type == 'image' && this.selectedMedia.embeddable) {
					return true;
				}
				return false;
			},

		},

		methods: {

			handleUploadSuccess(file, response) {

				// add file to media table in database
				axios.post(this.creativeAssetEndpoint +  '/media', {
					original_name: file.name,
					size: file.size,
					extension: file.extension,
					key: file.s3Multipart.key
				})
				.then((response) => {

					this.creativeAsset.media = response.data;

					this.resetSelectedMedia();
					this.resetPolling();

					// remove from files array
					for (var i = 0; i < this.files.length; i++)
					{
						if(this.files[i].id == file.id) {
							this.files.splice(i, 1);
						}
					}

					this.isReplacing = false;

				})
				.catch((error) => {
					console.error(error);
				});

			},

			fetchMedia() {
				axios.get(this.creativeAssetEndpoint +  '/media')
					.then((response) => {

						this.creativeAsset.media = response.data;

						this.resetSelectedMedia();

						if(this.isEmbeddableImage || this.isEmbeddableVideo) {
							clearInterval(this.polling); // won't be polled anymore
						}
					})
					.catch((error) => {
						console.error(error);
					});
			},

			resetPolling() {
				clearInterval(this.polling);
				if(!this.selectedMedia.embeddable && this.selectedMedia.processing_job_status != 'failed') {
					this.polling = setInterval(() => {
						this.fetchMedia()
					}, 3000) // check every 3 seconds
					setTimeout(() => { clearInterval(this.polling) }, 1000 * 60 * 30) //stop polling after 30 minutes
				}
			},

			resetSelectedMedia() {
				this.selectedMedia = this.creativeAsset.media[0];
			},

			openImageLightbox(el) {
				BigPicture({
					el: document.querySelector(el),
					imgSrc: this.selectedMedia.embeddable.url,
					noLoader: true,
				});
			},

			openVideoLightbox(el) {
				BigPicture({
					el: document.querySelector(el),
					vidSrc: this.selectedMedia.embeddable.url,
					noLoader: true,
				});
			},

			formatDate(date) {
				return dayjs(date).format('MMM D, YYYY h:mma');
			},

			formatBytes(bytes, decimals = 0) {
				if (bytes === 0) return '0 bytes';
				const k = 1024;
				const dm = decimals < 0 ? 0 : decimals;
				const sizes = ['bytes', 'kb', 'mb', 'gb', 'tb', 'pb', 'eb', 'zb', 'yb'];
				const i = Math.floor(Math.log(bytes) / Math.log(k));
				return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
			},

			isLowRes(width, height) {
				if(width > 0 && height > 0 && (width * height < 1920 * 1080)) {
					return true;
				}
				else {
					return false;
				}
			},

			nl2br(str, is_xhtml) {
				var breakTag = (is_xhtml || typeof is_xhtml === 'undefined') ? '<br />'   : '<br>';
				return (str + '').replace(/([^>\r\n]?)(\r\n|\n\r|\r|\n)/g, '$1' + breakTag   + '$2');
			},

			linkify(text) {
				var urlRegex = /(https?:\/\/[^\s]+)/g;
				return text.replace(urlRegex, function(url) {
					return '<a href="' + url + '" target="_blank" rel="noopener noreferrer">' + url + '</a>';
				});
			},

			strLimit(str, length) {
				if(str.length <= length) {
					return str;
				}

				return str.substring(0, length) + "...";
			}

		},

		beforeDestroy () {
			clearInterval(this.polling);
			uppy.close();
		}
	}

</script>
